import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useSite } from '../../../contexts/SiteContext';
import { CinematicVideo } from '../components/CinematicVideo';
import { PersonalSlideshow } from '../components/PersonalSlideshow';
import { OgMeta } from '../components/OgMeta';
import { proxyImg } from '../lib/imgProxy';

const BTTHomePage = () => {
  const { siteConfig } = useSite();
  
  const logoUrl = siteConfig?.theme?.logo || 'https://customer-assets.emergentagent.com/job_5fff2c07-dfff-4220-a200-8d4fd9f6d613/artifacts/i6xi2h68_BTTLogo.png';
  
  const experienceSteps = [
    { step: "01", title: "Define your project", description: "Define your project and creative vision." },
    { step: "02", title: "Develop materials", description: "Develop the core materials needed to communicate your project." },
    { step: "03", title: "Build a roadmap", description: "Build a roadmap for the next phase of production." },
    { step: "04", title: "Present & launch", description: "Present your project to the Community and identify next steps." }
  ];

  const quotes = [
    { quote: "Selina Ringel made my transition from screenwriter to screen director a wondrous and enlightening experience! Daily problems became daily solutions thanks to Selina Ringel. Thank you!", author: "Salvador Paskowitz", title: "Writer | The Age of Adaline" },
    { quote: "I called Selina with the crazy idea of producing a movie based on a true story with no prior experience. I learned everything from fundraising, script ideation, casting and more next to her! Selina truly puts her heart and soul into her work. She gives you everything she's got when she gets into a project. I am humbled to have worked with someone of such high integrity and so committed to my success. A truly great creative consultant but so much more.", author: "Victoire Cogevina", title: "Producer & Founder" },
    { quote: "Trusting Selina with guiding advice for a project was easy as she has so much experience and knowledge of the film industry. Not only is she fun and creative, but a pleasure to collaborate with. Selina has helped propel my projects to a higher level. She even gave me advice that helped me cut my budget in half and saved me a lot of time. Will use her expertise every time, highly recommend!", author: "Crystal Schainuck", title: "Show Host & Content Creator" },
    { quote: "Selina's notes on my screenplay provided me with great insights on how to tackle the next draft, which was infinitely better than the previous one. The screenplay then went on to make the semi-finals in the Blue Cat Screenplay Competition and is now in the finals, it is also now being optioned by a production company. I am extremely grateful for Selina's guidance.", author: "Alec James", title: "Comedian & Screenwriter" },
    { quote: "The fact that Selina Ringel was able to turn my small $10k budget into a fully produced half hour pilot presentation with a full-blown crew is nothing short of a miracle!", author: "Justin D.M. Palmer", title: "Producer" },
    { quote: "Professional, creative and fun to work with!!!", author: "Mireya Rios", title: "TikToker & Content Creator" },
    { quote: "When I got to L.A. I was faced with many challenges as I pursued my career in acting. The biggest one being that you needed a reel to get a job, but how would you have a reel if you hadn't worked before! Selina showed me that I had all the tools I needed to create my own content, and therefore my own path. Not only did she guide me through the entire process, we ended up winning international awards with the amazing content we created!", author: "Ivana De Maria", title: "Actress & Producer" },
    { quote: "Selina worked night and day to ensure the vision for the story came alive.", author: "Abi Corbin", title: "Director" },
    { quote: "Selina is an undeniable force of nature. The advice she gives is invaluable for anyone who struggles with self-doubt or needs some practical guidance in this crazy industry. I wish I had read this book when I was starting out as a young filmmaker in Hollywood!", author: "Amy Wang", title: "Director, Slanted | Writer, Crazy Rich Asians 2" },
    { quote: "Be the Train is a bold, no-nonsense guide for anyone ready to stop waiting and start building their success. If you're ready to stop waiting for opportunities and start making them, this book is for you.", author: "Diana Luna", title: "Executive Director, NALIP" },
    { quote: "Whether you're making your first film, launching a new career, or getting ready to take charge of your life, Be the Train will ignite the fire within you to make it happen.", author: "Mel Robbins", title: "#1 NYT Bestselling Author & Host of The Mel Robbins Podcast" }
  ];

  return (
    <div className="btt-home bg-black">
      <OgMeta
        title="Be The Train — A Creative Accelerator For Filmmakers Who Move"
        description="Be The Train is a creative accelerator for filmmakers and storytellers who are done asking for permission — and ready to build careers that move."
        image="https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/n8buf17t_BeTheTrain_HomePage.png"
        testId="og-meta-home"
      />

      {/* ===== 0. PERSONAL SLIDESHOW (lifestyle + rotating press testimonials) ===== */}
      <PersonalSlideshow />

      {/* ===== 1. HERO SECTION ===== */}
      <section data-testid="hero-section" className="relative min-h-[80vh] sm:min-h-[88vh] md:min-h-screen flex items-center bg-black px-6 md:px-12 lg:px-20 py-12 sm:py-20 md:py-32 lg:py-48">
        <div className="max-w-4xl">
          <h1 className="mb-8">
            <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1]" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              The train doesn't wait.
            </span>
            <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#00A86B] leading-[1.1] mt-2" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              Neither should you.
            </span>
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-white/65 font-light max-w-2xl mb-14 leading-relaxed">
            Be The Train is a creator ecosystem for filmmakers and storytellers who are done asking for permission — and ready to build careers that move.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-5">
            <Link
              to="/community"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00A86B] text-black font-bold uppercase tracking-wider hover:bg-[#00A86B]/90 transition-all"
              data-testid="hero-station-btn"
            >
              Join the Community — Free
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/apply"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/25 text-white font-bold uppercase tracking-wider hover:border-white/40 hover:bg-white/5 transition-all"
              data-testid="hero-apply-btn"
            >
              Apply to the Creator Lab
            </Link>
          </div>
          <p className="text-sm text-white/35 tracking-wide">
            No permission required.
          </p>
        </div>
      </section>

      {/* ===== 2. CREDIBILITY BAR ===== */}
      <section data-testid="credibility-bar" className="bg-[#0f0f0f] border-t border-white/[0.06] py-14 md:py-16 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-0">
          {[
            { value: '250+', label: 'Theaters Nationwide', accent: true },
            { value: 'Starz & HBO', label: 'Distribution Deals' },
            { value: '20+ Awards', label: 'Independent Films' },
            { value: 'Creator-Built', label: 'Built by Operators' },
          ].map((stat, idx) => (
            <div
              key={idx}
              className={`text-center lg:text-left lg:px-8 ${stat.accent ? 'lg:border-l-[3px] lg:border-l-[#00A86B]' : ''}`}
            >
              <p className="text-2xl md:text-3xl font-bold text-white leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                {stat.value}
              </p>
              <p className="text-[11px] text-white/40 uppercase tracking-[0.2em] mt-2">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== 2b. PERSONAL SLIDESHOW (lifestyle + rotating testimonials) ===== */}
      


      {/* ===== 3. AS FEATURED IN BAR ===== */}
      <section data-testid="featured-in-bar" className="border-y border-white/[0.06] py-12 px-6 bg-black">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-[11px] text-white/30 uppercase tracking-[0.35em] mb-9">As Featured In</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16 items-center justify-items-center">
            {[
              { name: 'Variety', src: 'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/0a1sd97v_images-14.png', blend: false, scale: 1.0 },
              { name: 'Deadline', src: 'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/azkxuce1_deadline-bg-blk-e1566863035200.png.webp', blend: true, scale: 1.55 },
              { name: 'The Hollywood Reporter', src: 'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/bx7oc065_Hollywood-Reporter-Logo-White.png', blend: false, scale: 1.0 },
              { name: 'IndieWire', src: 'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/k4oexq06_channels4_profile.jpg', blend: true, scale: 1.9 },
            ].map((logo) => (
              <img
                key={logo.name}
                src={proxyImg(logo.src, { w: 220 })}
                alt={logo.name}
                loading="lazy"
                decoding="async"
                data-testid={`featured-${logo.name.toLowerCase().replace(/\s+/g, '-')}`}
                style={{
                  maxWidth: '160px',
                  maxHeight: '70px',
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  opacity: 0.85,
                  // Per-logo visual scale: IndieWire +40%, Deadline +30%
                  transform: `scale(${logo.scale})`,
                  transformOrigin: 'center',
                  // 'screen' + contrast crush neutralises baked-in dark-gray
                  // backgrounds (Deadline WebP / IndieWire profile JPG)
                  mixBlendMode: logo.blend ? 'screen' : 'normal',
                  filter: logo.blend ? 'contrast(1.6) brightness(1.1)' : 'none',
                  transition: 'opacity .25s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.85'; }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 3a. MEET THE FILMMAKERS — CINEMATIC INTRO ===== */}
      <section
        data-testid="meet-the-filmmakers-section"
        className="relative px-6 py-28 md:py-40 bg-zinc-950 border-b border-white/[0.06] overflow-hidden"
      >
        {/* Subtle radial glow backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 50% at 50% 30%, rgba(0,168,107,0.10) 0%, transparent 60%)',
          }}
        />
        <div className="relative max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#00A86B] text-[11px] uppercase tracking-[0.4em] mb-5">
              The People Behind The Movement
            </p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              Meet the filmmakers <br className="hidden sm:block" />
              behind Be The Train.
            </h2>
            <p className="text-white/55 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              After years of building films, businesses, books, and community together, Dan and Selina realized the biggest obstacle facing creators wasn&apos;t talent — it was waiting. Be The Train was built to help creators build momentum, ownership, and careers that move.
            </p>
          </div>

          <CinematicVideo
            videoId="8Uc4OWk27zQ"
            provider="youtube"
            title="Meet the filmmakers behind Be The Train"
            posterSrc="https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/n8buf17t_BeTheTrain_HomePage.png"
            kicker="Watch · 2 min"
            caption="Dan Levy Dagerman &amp; Selina Ringel — Founders"
            testId="homepage-founder-video"
          />

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#00A86B] text-black font-bold uppercase tracking-wider text-xs hover:bg-[#00A86B]/90 transition-all"
              data-testid="meet-filmmakers-about-btn-primary"
            >
              More About Dan &amp; Selina <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 3a2. START THE ENGINE — FEATURED LAUNCH ===== */}
      <section
        data-testid="ste-launch-section"
        className="relative px-6 py-24 md:py-32 border-t border-white/[0.06] overflow-hidden"
        style={{
          background:
            'radial-gradient(55% 60% at 80% 50%, rgba(0,168,107,0.18) 0%, transparent 60%), linear-gradient(180deg, #050505 0%, #000 100%)',
        }}
      >
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center">
          {/* Cover with cinematic glow */}
          <div className="relative flex justify-center lg:justify-start">
            <div className="relative">
              <div className="absolute -inset-12 sm:-inset-16 rounded-full bg-[#00A86B]/25 blur-[100px]" aria-hidden="true" />
              <div className="absolute -inset-3 rounded-full bg-white/[0.05] blur-[30px]" aria-hidden="true" />
              <img
                src={proxyImg("https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/4q4ui3ww_Screenshot%202026-06-04%20at%207.31.31%20AM.png", { w: 360 })}
                alt="Start The Engine — book cover"
                className="relative w-56 sm:w-64 md:w-72 shadow-[0_40px_100px_rgba(0,0,0,0.9)]"
                decoding="async"
                loading="eager"
                data-testid="ste-launch-cover"
              />
              <span className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 px-3 py-1.5 bg-[#00A86B] text-black text-[10px] font-bold uppercase tracking-[0.2em]">
                Just Launched
              </span>
            </div>
          </div>

          {/* Copy + CTA */}
          <div>
            <p className="text-[#00A86B] text-[11px] uppercase tracking-[0.4em] mb-5">
              The New Book
            </p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.02] mb-6"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              Start The Engine.
            </h2>
            <p className="text-white/70 text-lg sm:text-xl leading-relaxed mb-5 max-w-xl">
              The creator manifesto for a world that no longer works the way it used to.
            </p>
            <p className="text-white/45 leading-relaxed mb-9 max-w-xl">
              Filmmakers. Creators. Entrepreneurs. Community builders. The walls between them have collapsed. <em>Start The Engine</em> is the field guide for navigating the new creator era — and building a life around your work.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-7">
              <a
                href="https://go.bethetrain.com/digital-ebook-payment-page-6795"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00A86B] text-black font-bold uppercase tracking-wider hover:bg-white transition-colors"
                data-testid="ste-launch-buy-btn"
              >
                Get The Book — Digital Edition
                <ArrowRight size={16} />
              </a>
              <Link
                to="/books/start-the-engine"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white/85 font-bold uppercase tracking-wider hover:border-white/40 hover:text-white transition-all"
                data-testid="ste-launch-learn-btn"
              >
                Learn More
              </Link>
            </div>

            <p className="text-[11px] uppercase tracking-[0.3em] text-white/40">
              Selina Ringel &amp; Dan Levy Dagerman &nbsp;·&nbsp; Digital edition · Instant access
            </p>
          </div>
        </div>
      </section>

      {/* ===== 3b. FEATURED RESOURCES ===== */}
      <section data-testid="featured-resources-section" className="py-24 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <p className="btt-kicker text-[#00A86B] text-sm uppercase tracking-[0.2em] mb-3">Start Here</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12">Featured Resources</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { kicker: 'The Manifesto', title: 'Start The Engine', desc: 'The creator manifesto. Stop waiting. Start building.', href: '/books/start-the-engine', badge: 'NEW' },
              { kicker: 'The Book', title: 'Be The Train', desc: 'The foundational book — mindset and tools to make your first feature.', href: '/book' },
              { kicker: 'Case Study', title: 'You, Me & Her', desc: 'How we launched a film into 250+ theaters without a distributor.', href: '/case-studies/you-me-and-her' },
              { kicker: 'The Podcast', title: 'Be The Train Podcast', desc: 'Conversations with creators who refuse to wait for permission.', href: '/podcast' },
            ].map((card) => (
              <Link
                key={card.href}
                to={card.href}
                className="group block p-7 bg-zinc-950 border border-white/[0.08] hover:border-[#00A86B]/50 transition-colors"
                data-testid={`featured-resource-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <p className="text-[10px] text-white/40 uppercase tracking-[0.25em]">{card.kicker}</p>
                  {card.badge && (
                    <span className="px-1.5 py-0.5 bg-[#00A86B] text-black text-[9px] font-bold uppercase tracking-wider leading-none">
                      {card.badge}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-white mb-3 group-hover:text-[#00A86B] transition-colors">{card.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed mb-5">{card.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-[#00A86B] text-xs uppercase tracking-wider group-hover:gap-2.5 transition-all">
                  Explore <ArrowRight size={13} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4. PHILOSOPHY SECTION ===== */}
      <section data-testid="philosophy-section" className="py-16 md:py-32 px-6 bg-zinc-950">
        <div className="max-w-4xl mx-auto">
          <p className="btt-kicker text-[#00A86B] text-sm uppercase tracking-[0.2em] mb-6 text-center">The Philosophy</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center mb-12 leading-tight">
            Stop waiting at the station.<br/>
            <span className="text-white/60">Be the train.</span>
          </h2>
          <div className="space-y-6 text-lg text-white/70 leading-relaxed">
            <p>
              For years, filmmaker Selina Ringel followed the traditional path — film school, auditions, 
              industry gatekeepers — waiting for opportunities to arrive.
            </p>
            <p>
              They didn't.
            </p>
            <p>
              So she built her own train.
            </p>
            <p>
              She began writing, producing, and starring in her own films, creating momentum from the ground up. 
              That mindset became Be The Train, a philosophy for creators who refuse to wait for permission.
            </p>
            <p className="text-white/50">
              Today, Be The Train has grown beyond a book into a growing ecosystem where creators build momentum together.
            </p>
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/philosophy"
              className="inline-flex items-center gap-2 text-[#00A86B] hover:text-[#00A86B]/80 transition-colors"
            >
              Read the full philosophy
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 5. WHO THIS IS FOR SECTION ===== */}
      <section data-testid="who-this-is-for-section" className="py-16 md:py-32 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <p className="btt-kicker text-[#00A86B] text-sm uppercase tracking-[0.2em] mb-6">Who This Is For</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-16">
            Built for creators at any stage who are ready to move.
          </h2>
          <div className="space-y-8 mb-14">
            <p className="text-lg text-white/70 leading-relaxed">For anyone with a dream.</p>
            <p className="text-lg text-white/70 leading-relaxed">For anyone who has imagined writing a book, starting a podcast, creating a short, building a web series, or making a film.</p>
            <p className="text-lg text-white/70 leading-relaxed">For people who want to bring the message they care about into the world — and discover themselves along the way.</p>
            <p className="text-lg text-white/70 leading-relaxed">For creators ready to stop waiting — and start building.</p>
          </div>
          <p className="text-xs text-white/30 uppercase tracking-[0.2em]">
            Filmmakers · Writers · Podcasters · Creators · Entrepreneurs · Storytellers
          </p>
        </div>
      </section>

      {/* ===== 5.5. LALIFF BOOK-SIGNING STILL — community proof above testimonials ===== */}
      <figure
        className="relative w-full bg-black overflow-hidden"
        data-testid="laliff-still"
      >
        <div className="aspect-[16/9] sm:aspect-[21/9] lg:aspect-[24/9] relative">
          <img
            src={proxyImg("https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/mwon5473_BTT_BookSigningLaLiff.jpg", { w: 1400 })}
            alt="Tú Eres El Tren book signing at LALIFF — a packed room of filmmakers, creators, and supporters"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </figure>

      {/* ===== 6. WHAT PEOPLE ARE SAYING ===== */}
      <section
        data-testid="what-people-are-saying-section"
        className="relative py-16 md:py-32 px-6 bg-zinc-950 overflow-hidden"
      >
        {/* Stickers wall — decorative background (dim, dark-mode overlay) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `url("${proxyImg("https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/sw01bdbo_2.jpg", { w: 1400 })}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.32,
            filter: 'saturate(0.85)',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(9,9,11,0.88) 0%, rgba(9,9,11,0.65) 50%, rgba(9,9,11,0.92) 100%)',
          }}
        />
        <div className="relative max-w-6xl mx-auto">
          <p className="btt-kicker text-[#00A86B] text-sm uppercase tracking-[0.2em] mb-6">What People Are Saying</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-16">
            What People Are Saying
          </h2>
          <div className="grid md:grid-cols-2 gap-6" style={{ alignItems: 'start' }}>
            {quotes.map((item, idx) => (
              <div key={idx} className="p-8 bg-black/70 backdrop-blur-sm border border-white/[0.08]">
                <blockquote className="text-white/70 mb-6 leading-relaxed">
                  &ldquo;{item.quote}&rdquo;
                </blockquote>
                <p className="text-white font-bold">{item.author}</p>
                <p className="text-white/40 text-sm">{item.title}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/press" className="inline-flex items-center gap-2 text-[#00A86B] text-sm hover:text-[#00A86B]/80 transition-colors">
              See all press &amp; endorsements <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 8. CREATOR LAB SECTION ===== */}
      <section data-testid="creator-lab-section" className="py-16 md:py-32 px-6 bg-zinc-950">
        <div className="max-w-3xl mx-auto">
          <div>
            <p className="btt-kicker text-[#00A86B] text-sm uppercase tracking-[0.2em] mb-6">Creator Lab</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              The Creator Lab
            </h2>
            <p className="text-lg text-white/70 mb-8 leading-relaxed">
              The Creator Lab is Be The Train's project accelerator. Over several weeks, filmmakers
              work through the Be The Train methodology to develop their next project and build the
              materials needed to move it forward.
            </p>
            <p className="text-white/50 mb-6">Participants develop:</p>
            <ul className="space-y-3 mb-8">
              {[
                "A clearly defined project concept",
                "A pitch deck and development materials",
                "A production roadmap",
                "A network of collaborators who move"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-white/70">
                  <CheckCircle className="w-5 h-5 text-[#00A86B] flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/apply"
              className="inline-flex items-center gap-2 text-[#00A86B] hover:text-[#00A86B]/80 transition-colors"
            >
              Apply to the Creator Lab
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 10. THE BOOK SECTION ===== */}
      <section data-testid="book-section" className="py-32 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <p className="btt-kicker text-[#00A86B] text-sm uppercase tracking-[0.2em] mb-6">The Book</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                The Mindset and Tools You Need to Make Your First Feature Film
              </h2>
              <p className="text-lg text-white/70 mb-8 leading-relaxed">
                Be The Train began as a book by filmmaker Selina Ringel — a practical guide to 
                launching your first film and building momentum as a creator.
              </p>
              <p className="text-white/50 mb-8 leading-relaxed">
                Drawing from her experience writing, producing, and starring in her own projects, 
                Selina shares the mindset and tools that helped her stop waiting and start building.
              </p>
              <a
                href="https://www.amazon.com/Be-Train-Mindset-Tools-Feature/dp/B0DVBYB1KJ"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-bold uppercase tracking-wider hover:bg-white/90 transition-all"
              >
                Get the Book
                <ArrowRight size={18} />
              </a>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <img
                src={proxyImg("https://customer-assets.emergentagent.com/job_c616a873-a54c-4989-849b-bca749e896c0/artifacts/utq8zxa8_BeTheTrain_Cover_v6.jpg", { w: 360 })}
                alt="Be The Train Book"
                className="w-64 md:w-80 shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== 12. ABOUT SELINA & DAN SECTION ===== */}
      <section data-testid="about-section" className="py-16 md:py-32 px-6 bg-zinc-950">
        <div className="max-w-5xl mx-auto">
          {/* Cinematic founders still — sits above THE FOUNDERS kicker */}
          <figure className="mb-12 md:mb-16" data-testid="founders-still">
            <div className="relative bg-black mx-auto" style={{ maxWidth: '720px' }}>
              <img
                src={proxyImg("https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/m2gknf4s_DanSelinaNY.jpeg", { w: 720 })}
                alt="Selina Ringel and Dan Levy Dagerman under the Village East Cinema marquee — You, Me & Her theatrical run, New York"
                className="block w-full h-auto"
                loading="lazy"
              />
            </div>
          </figure>
          <div>
            <p className="btt-kicker text-[#00A86B] text-sm uppercase tracking-[0.2em] mb-6">The Founders</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              About Selina & Dan
            </h2>
            <p className="text-lg text-white/70 mb-6 leading-relaxed">
              Selina Ringel and Dan Levy Dagerman are the founders of Be The Train — filmmakers and operators who built their careers by creating their own opportunities.
            </p>
            <p className="text-white/50 mb-10 leading-relaxed">
              Their most recent feature <em>You, Me &amp; Her</em> released in 250+ theaters nationwide and sold to STARZ and HBO Max.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-[#00A86B] hover:text-[#00A86B]/80 transition-colors"
            >
              Read full bio
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 13. FINAL CTA SECTION ===== */}
      <section data-testid="final-cta-section" className="py-16 md:py-40 px-6 bg-gradient-to-b from-zinc-950 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Every career starts with a decision to move.
          </h2>
          <p className="text-lg text-white/60 mb-14 max-w-2xl mx-auto">
            Be The Train is where that movement begins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/community"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00A86B] text-black font-bold uppercase tracking-wider hover:bg-[#00A86B]/90 transition-all"
              data-testid="final-cta-station-btn"
            >
              Join the Community — Free
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/apply"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/25 text-white font-bold uppercase tracking-wider hover:border-white/40 hover:bg-white/5 transition-all"
              data-testid="final-cta-apply-btn"
            >
              Apply to the Creator Lab
              <ArrowRight size={18} />
            </Link>
          </div>
          <p className="text-white/40 text-sm mt-8">
            Not sure where to start?{' '}
            <Link to="/book-a-call" className="text-[#00A86B] hover:text-[#00A86B]/80 transition-colors">
              Book a call with Selina first &rarr;
            </Link>
          </p>
        </div>
      </section>
      
    </div>
  );
};

export default BTTHomePage;
